import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getCoachHealthEvidenceDestinationAuthorization,
  getCoachHealthRelationshipDestinationAuthorization,
  type CoachHealthRelationshipAuthorizationResult,
} from "@/lib/coachHealth/authorizeCoachHealthEvidenceDestination";
import type { CoachHealthEvidenceRouteIdentity } from "@/lib/coachHealth/coachHealthEvidenceRoute";

const FOREGROUND_REFETCH_MS = 30_000;
const MAX_TIMEOUT_MS = 2_147_000_000;

export type CoachHealthDestinationAuthorizationTarget =
  | {
      readonly kind: "relationship";
      readonly leaderId: string;
    }
  | {
      readonly kind: "evidence";
      readonly identity: CoachHealthEvidenceRouteIdentity;
      readonly destinationRoute: string;
    }
  | { readonly kind: "invalid" };

export type CoachHealthReauthorizationResult =
  | {
      readonly status: "authorized";
      readonly authorized: true;
      readonly relationshipRevision: string;
    }
  | {
      readonly status: "revoked";
      readonly authorized: false;
      readonly relationshipRevision: string | null;
    }
  | {
      readonly status: "retryable_error";
      readonly authorized: false;
      readonly relationshipRevision: null;
    };

function targetKey(target: CoachHealthDestinationAuthorizationTarget | null) {
  if (!target) return ["disabled"] as const;
  if (target.kind === "invalid") return ["invalid"] as const;
  if (target.kind === "relationship") {
    return ["relationship", target.leaderId] as const;
  }
  return [
    "evidence",
    target.identity.sourceType,
    target.identity.sourceId,
    target.identity.leaderId,
    target.identity.relationshipRevision,
    target.identity.routeBindingId,
    target.destinationRoute,
  ] as const;
}

type HardGateState =
  | { readonly kind: "none"; readonly at: null }
  | { readonly kind: "valid"; readonly at: number }
  | { readonly kind: "malformed"; readonly at: null };

function parseHardGateAt(value: unknown): HardGateState {
  if (value === null) return { kind: "none", at: null };
  if (typeof value !== "string") return { kind: "malformed", at: null };
  const at = Date.parse(value);
  return Number.isFinite(at)
    ? { kind: "valid", at }
    : { kind: "malformed", at: null };
}

export function useCoachHealthDestinationAuthorization({
  coachId,
  target,
  enabled = true,
}: {
  readonly coachId?: string | null;
  readonly target: CoachHealthDestinationAuthorizationTarget | null;
  readonly enabled?: boolean;
}) {
  const canQuery = Boolean(
    enabled && coachId && target && target.kind !== "invalid",
  );
  const activationKey = JSON.stringify([
    enabled ? "enabled" : "disabled",
    coachId ?? null,
    ...targetKey(target),
  ]);
  const query = useQuery({
    queryKey: [
      "coach-health-destination-authorization",
      coachId ?? null,
      ...targetKey(target),
    ],
    queryFn: async (): Promise<CoachHealthRelationshipAuthorizationResult> => {
      if (!coachId || !target || target.kind === "invalid") {
        return {
          status: "revoked",
          authorized: false,
          nextHardGateAt: null,
          relationshipRevision: null,
        };
      }
      if (target.kind === "relationship") {
        return getCoachHealthRelationshipDestinationAuthorization({
          coachId,
          leaderId: target.leaderId,
        });
      }
      const evidence = await getCoachHealthEvidenceDestinationAuthorization({
        coachId,
        identity: target.identity,
        destinationRoute: target.destinationRoute,
      });
      if (evidence.status === "retryable_error") {
        return {
          status: "retryable_error",
          authorized: false,
          nextHardGateAt: null,
          relationshipRevision: null,
        };
      }
      if (evidence.status === "unavailable") {
        return {
          status: "revoked",
          authorized: false,
          nextHardGateAt: null,
          relationshipRevision: null,
        };
      }
      return {
        status: "authorized",
        authorized: true,
        nextHardGateAt: null,
        relationshipRevision: evidence.item.relationshipRevision,
      };
    },
    enabled: canQuery,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    refetchInterval: (currentQuery) => {
      const boundary = currentQuery.state.data?.nextHardGateAt;
      const hardGate = parseHardGateAt(boundary);
      if (hardGate.kind !== "valid") return FOREGROUND_REFETCH_MS;
      const remaining = hardGate.at - Date.now();
      return Math.min(FOREGROUND_REFETCH_MS, Math.max(1, remaining));
    },
  });

  const [, setHardGateTick] = useState(0);
  const activationRef = useRef<{
    key: string;
    baselineDataUpdatedAt: number;
    startedFetch: boolean;
    hasFreshResult: boolean;
  } | null>(null);
  if (activationRef.current?.key !== activationKey) {
    activationRef.current = {
      key: activationKey,
      baselineDataUpdatedAt: query.dataUpdatedAt,
      startedFetch: false,
      hasFreshResult: false,
    };
  }
  const activation = activationRef.current;
  if (activation && query.isFetching) {
    activation.startedFetch = true;
  }
  if (
    activation &&
    activation.startedFetch &&
    query.isSuccess &&
    !query.isFetching &&
    // The fetch transition proves freshness even when cache and response share
    // the same millisecond timestamp.
    query.dataUpdatedAt >= activation.baselineDataUpdatedAt
  ) {
    activation.hasFreshResult = true;
  }
  const invalid = enabled && target?.kind === "invalid";
  const hardGate = parseHardGateAt(query.data?.nextHardGateAt);
  const hardGateExpired =
    hardGate.kind === "valid" && hardGate.at <= Date.now();
  useEffect(() => {
    if (hardGate.kind !== "valid" || hardGate.at <= Date.now()) return;
    const remaining = hardGate.at - Date.now();
    if (remaining > MAX_TIMEOUT_MS) return;
    const timer = window.setTimeout(() => {
      setHardGateTick((tick) => tick + 1);
    }, Math.max(0, remaining));
    return () => window.clearTimeout(timer);
  }, [hardGate.at, hardGate.kind]);
  const currentKeyAuthorized = Boolean(
    enabled &&
      target &&
      !invalid &&
      query.isSuccess &&
      !query.isError &&
      activation?.hasFreshResult === true &&
      query.data?.authorized === true &&
      hardGate.kind !== "malformed",
  );
  const safeBackgroundFetch = Boolean(
    currentKeyAuthorized && query.isFetching && !hardGateExpired,
  );
  const isChecking = Boolean(
    enabled &&
      target &&
      !invalid &&
      (query.isLoading ||
        (query.isSuccess && !query.isError && activation?.hasFreshResult !== true) ||
        hardGateExpired ||
        (query.isFetching && !safeBackgroundFetch)),
  );
  const isAuthorized = Boolean(currentKeyAuthorized && !hardGateExpired);
  const isRetryableError = Boolean(
    enabled &&
      target &&
      !isChecking &&
      !invalid &&
      (query.isError || query.data?.status === "retryable_error"),
  );
  const isUnavailable = Boolean(
    enabled &&
      target &&
      !isChecking &&
      (invalid ||
        hardGate.kind === "malformed" ||
        (query.isSuccess &&
          query.data?.status !== "retryable_error" &&
          query.data?.authorized !== true)),
  );
  const relationshipRevision = query.data?.relationshipRevision ?? null;
  const accessIdentityState = isAuthorized
    ? "authorized"
    : isRetryableError
      ? "retryable_error"
    : hardGateExpired ||
        isUnavailable ||
        (query.isSuccess &&
          query.data?.status !== "retryable_error" &&
          query.data?.authorized !== true)
      ? "unavailable"
      : "checking";
  const accessFingerprint = JSON.stringify([
    activationKey,
    relationshipRevision,
    accessIdentityState,
  ]);
  const accessGenerationRef = useRef({
    fingerprint: accessFingerprint,
    generation: 0,
  });
  if (accessGenerationRef.current.fingerprint !== accessFingerprint) {
    accessGenerationRef.current = {
      fingerprint: accessFingerprint,
      generation: accessGenerationRef.current.generation + 1,
    };
  }
  const accessGeneration = `${activationKey}:${accessGenerationRef.current.generation}`;
  const refetch = query.refetch;

  const reauthorizeAccess = useCallback(async (): Promise<CoachHealthReauthorizationResult> => {
    if (!canQuery || invalid) {
      return { status: "revoked", authorized: false, relationshipRevision: null };
    }
    try {
      const result = await refetch();
      if (result.error || !result.data) {
        return { status: "retryable_error", authorized: false, relationshipRevision: null };
      }
      if (result.data.status === "retryable_error") {
        return { status: "retryable_error", authorized: false, relationshipRevision: null };
      }
      const resultHardGate = parseHardGateAt(result.data?.nextHardGateAt);
      const relationshipRevision = result.data.relationshipRevision ?? null;
      const authorized = Boolean(
        result.data.authorized === true &&
          relationshipRevision &&
          resultHardGate.kind !== "malformed" &&
          (resultHardGate.kind !== "valid" || resultHardGate.at > Date.now()),
      );
      if (!authorized || !relationshipRevision) {
        return { status: "revoked", authorized: false, relationshipRevision };
      }
      return { status: "authorized", authorized: true, relationshipRevision };
    } catch {
      return { status: "retryable_error", authorized: false, relationshipRevision: null };
    }
  }, [canQuery, invalid, refetch]);
  const reauthorize = useCallback(async () => (
    await reauthorizeAccess()
  ).authorized, [reauthorizeAccess]);

  return {
    isAuthorized,
    isChecking,
    isUnavailable,
    isRetryableError,
    relationshipRevision,
    accessGeneration,
    reauthorize,
    reauthorizeAccess,
  };
}
